import { getRolesWithAttribute } from '@src/entities/User/includes/permissions';
import { useCurrentUser } from '@src/entities/User/ui/hooks/useCurrentUser';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { isEmpty, isNil } from 'lodash';
import React, { Children, cloneElement, ReactNode, useMemo } from 'react';

import { Styles } from './includes/styles';

type CapabilityProps = {
  alias?: string;
  attributes?: string[];
  children: ReactNode;
  fallback?: null | ReactNode;
  isAllowed?: boolean | null;
  method?: 'disable' | 'hide';
  resource?: string;
};

type CapabilityState = {
  attribute: string;
  isAllowed: boolean | null;
  message: string;
};

/**
 * Capability
 *
 * When the button is disabled, wrap it in a Tooltip explaining why it is disabled.
 */
export function Capability({
  alias = '',
  attributes = [],
  children,
  fallback = null,
  isAllowed = null,
  method = 'disable',
  resource = '',
}: CapabilityProps) {
  const { hasPermission } = useCurrentUser();

  const capability = useMemo<CapabilityState>(() => {
    const theCapability: CapabilityState = {
      attribute: '',
      isAllowed,
      message: '',
    };

    const allowedRoles: string[] = [];
    const allowedAttributes: Record<string, boolean> = {};

    attributes.forEach((attribute) => {
      theCapability.attribute = attribute;

      allowedAttributes[attribute] = hasPermission(resource, attribute);

      const rolesWithAttribute: string[] = getRolesWithAttribute(resource, attribute, true);

      rolesWithAttribute.forEach((role) => {
        if (!allowedRoles.includes(role)) {
          allowedRoles.push(role);
        }
      });
    });

    if (!isEmpty(alias)) {
      theCapability.attribute = alias;
    }

    theCapability.isAllowed = Object.values(allowedAttributes).every((value) => {
      return value;
    });

    if (!theCapability.isAllowed) {
      if (isEmpty(allowedRoles)) {
        theCapability.message = `There are no roles with the required permissions to access this feature.`;
      } else {
        theCapability.message = `Required Permissions: ${allowedRoles.join(' Access, ')} Access`;
      }
    }

    return theCapability;
  }, [resource, attributes, isAllowed, alias]);

  return (
    <ErrorHandler location="src/entities/User/ui/components/Capability/Capability.tsx">
      {method === 'hide' && capability.isAllowed === true ? children : !isNil(fallback) && fallback}

      {method === 'disable' &&
        (capability.isAllowed === true ? (
          children
        ) : (
          <Styles>
            <Tooltip asChild={false} content={capability.message} css={{ width: '100%' }}>
              {Children.map(children, (child) => {
                if (!React.isValidElement(child)) {
                  return child;
                }
                return cloneElement(child, {
                  // @ts-ignore
                  ...child.props,
                  className: `${(child.props as any)?.className ?? ''} isDisabled-true`,
                  disabled: true,
                  isDisabled: true,
                });
              })}
            </Tooltip>
          </Styles>
        ))}
    </ErrorHandler>
  );
}
